/**
 * Menu and mode, one at a time.
 *
 * The shell owns exactly one transition: the menu is up, or a mode is running, or the co-op lobby
 * is open. Whichever is not showing is fully torn down, so no menu backdrop keeps a WebGL context
 * alive behind a running game and no mode keeps rendering behind the menu.
 *
 * It knows nothing about any particular mode. It reads the registry, hands a mode a container, a
 * session and a way back, and waits.
 *
 * A mode's code is not here when the menu is. It is downloaded on the press that asks for it, so
 * the shell holds the wait as well as the transition: the menu stays up and inert until both the
 * chunk and the session have landed, and a chunk that never arrives leaves the player on the screen
 * they pressed from rather than on a dead one.
 *
 * Every launch carries a session, the menu's included: solo opens a loopback transport and plays
 * against nobody. That is what keeps the game free of an "if we are online" branch, and it means
 * the path a player exercises alone is the path multiplayer uses. It carries a seed too, rolled
 * per launch the way a co-op session takes its join code, so playing alone is not playing one
 * level. See `soloSeed.ts` for what pins it back down.
 *
 * The way back carries a reason. A session can end because somebody chose to leave or because the
 * host disappeared, and those need different words on the menu, so the menu is told which it was
 * rather than a player arriving there with no explanation and reading it as a crash.
 *
 * A link can press a card. `?mode=<id>` resolves through the registry and the shell launches it
 * itself, which is a raid or hub URL anyone can paste and the only way a tool reaches a mode it did
 * not write. The menu is opened first rather than skipped: `launch` needs a screen to hold the wait
 * on and to fail back to, so a link to a mode that will not download lands somewhere a player can
 * act rather than on a dead page. Read once, on start, and never re-entered: leaving still goes to
 * the menu.
 *
 * Settings are the one screen that is not a transition. They are raised over whatever is showing,
 * menu or running game, and nothing is torn down underneath: the point of a knob that applies
 * immediately is that the thing it changes is still there to be judged while it changes. Escape is
 * how a locked pointer reaches it, which works because the browser has already dropped the lock by
 * the time the key arrives.
 *
 * A guest that loses the host does not take that way back straight away. The mode keeps running, a
 * countdown goes over it, and the same code is knocked on for as long as the seat is being kept. If
 * the host is alive the connection is replaced underneath a game that never stopped: no scene
 * rebuilt, no physics loaded, no nav map built again, and the world resumes on the next keyframe.
 * Only once the budget is spent is this a trip to the menu.
 */

import { SAVE_NO_STORAGE } from '@/text/saveText';
import { createCoopLobby } from '@/ui/lobby/coopLobby';
import { createMainMenu } from '@/ui/menu/mainMenu';
import { createReconnectOverlay } from '@/ui/net/reconnectOverlay';
import { createSaveTransfer } from '@/ui/save/saveTransferPanel';
import { applyAccessibilityOverride } from '@/dev/accessibilityHost';
import { applyHudPresentationOverride } from '@/dev/hudPresentationHost';
import { applyVideoSettingsOverride } from '@/dev/videoSettingsHost';
import { createSettingSession } from '@/app/settingSession';
import { createSettingsScreen } from '@/ui/settings/settingsScreen';
import { SETTINGS } from '@/ui/settings/settingRegistry';
import { createUpgradePanel } from '@/ui/progression/upgradePanel';
import { createSessionNotices } from '@/ui/net/sessionNotices';
import { createMenuBackdrop } from '@/app/menuBackdrop';
import { modeFromUrl } from '@/app/modeUrl';
import { MODE_UNAVAILABLE } from '@/text/menuText';
import { findMode, MODES } from '@/modes/modeRegistry';
import { netErrorAdvice, netErrorMessage } from '@/net/session/sessionErrors';
import { openSoloSession, sessionFromLobby } from '@/net/session/netSession';
import { SEAT_HOLD_MS } from '@/net/session/sessionLimits';
import { soloSeed } from '@/app/soloSeed';
import { watchSeatChange } from '@/app/seatChangeWatch';
import { watchSessionEnd } from '@/app/sessionEndWatch';
import type { CoopLobby } from '@/ui/lobby/coopLobby';
import type { ReconnectOverlay } from '@/ui/net/reconnectOverlay';
import type { SettingsScreen } from '@/ui/settings/settingsScreen';
import type { ImportNews, SaveTransfer } from '@/ui/save/saveTransferPanel';
import type { UpgradePanel } from '@/ui/progression/upgradePanel';
import type { SeatChange } from '@/app/seatChangeWatch';
import type { ExitReason, GameMode, ModeCode, ModeSession } from '@/modes/modeContract';
import type { MainMenu, MenuNotice } from '@/ui/menu/mainMenu';
import type { NetErrorCode } from '@/net/session/sessionErrors';
import type { LobbySession } from '@/net/lobby/lobbySession';
import type { NetSession } from '@/net/session/netSession';
import { profileSheet } from '@/save/profileSheet';
import type { ProfileSession } from '@/save/profileSession';
import type { SessionNotices } from '@/ui/net/sessionNotices';

/**
 * What the lobby screen was opened to do.
 *
 * A guest has no mode to name until the host says, and a guest coming back has no code to type: it
 * is already in a session whose seat is being kept for it.
 */
export type LobbyIntent =
  | { readonly kind: 'host'; readonly mode: GameMode }
  | { readonly kind: 'join'; readonly code: string }
  | { readonly kind: 'rejoin' };

export interface AppShell {
  /** Opens the menu. Returns a function that tears down whatever is showing. */
  start(): () => void;
}

export function createAppShell(container: HTMLElement, profile: ProfileSession): AppShell {
  // Before anything is built, because a knob is only honest if the game was already running on it:
  // a sensitivity applied after the first frame is a sensitivity that changed under the player.
  const settings = createSettingSession(profile, SETTINGS);
  settings.applyAll();
  // After the profile, because in development a shot's URL is the more specific instruction: a
  // frame of the conventional HUD at the top of the scale is what was asked for whatever character
  // the tool happened to open. Folded out of a build entirely, so a player only has the knobs.
  applyHudPresentationOverride();
  applyAccessibilityOverride();
  applyVideoSettingsOverride();

  let menu: MainMenu | null = null;
  let settingsScreen: SettingsScreen | null = null;
  let settingsSave: SaveTransfer | null = null;
  let saveTransfer: SaveTransfer | null = null;
  let upgrade: UpgradePanel | null = null;
  let session: ModeSession | null = null;
  let lobby: CoopLobby | null = null;
  let notices: SessionNotices | null = null;
  let overlay: ReconnectOverlay | null = null;
  let stopWatching: (() => void) | null = null;
  // Owns its own lifetime: the scene arrives after the menu it sits behind, and the lobby keeps it
  // while the menu itself is disposed.
  const scenery = createMenuBackdrop(container);
  // Kept across a running game, because a guest that loses the host has to be able to knock on the
  // same door again, and the session is the only thing that still knows which door it was.
  let live: LobbySession | null = null;
  // Bumped on every teardown, so a session that finished opening after the player left goes
  // nowhere rather than launching a mode into a container the shell has moved on from.
  let generation = 0;

  const closeMenu = (): void => {
    menu?.dispose();
    menu = null;
    saveTransfer?.dispose();
    saveTransfer = null;
    upgrade?.dispose();
    upgrade = null;
    scenery.hide();
  };

  const closeSettings = (): void => {
    settingsScreen?.dispose();
    settingsScreen = null;
    settingsSave?.dispose();
    settingsSave = null;
  };

  /**
   * Settings, over whatever is showing.
   *
   * Nothing is torn down. From the menu that means the menu is still behind it; from a game it
   * means the mode is still stepping, which is the only way a sensitivity slider can be judged
   * while it is being dragged.
   *
   * The save panel comes along only when no game is running. It is a second instance of the same
   * component the menu builds rather than the menu's own element moved across, because an element
   * moved out of a live screen has to be moved back into it, and a panel is cheap to build twice
   * while a half-finished import is not. Mid-game it is left out entirely: importing a file
   * replaces the character the running mode is already playing.
   */
  const openSettings = (): void => {
    if (settingsScreen !== null) return;
    if (session === null) settingsSave = createSaveTransfer({ profile, onImported: importedHere });
    settingsScreen = createSettingsScreen(container, {
      definitions: SETTINGS,
      value: (definition) => settings.value(definition),
      onChange: (definition, value): void => {
        settings.apply(definition, value);
      },
      onCommit: (definition, value): void => {
        settings.set(definition, value);
      },
      onClose: closeSettings,
      saveTransfer: settingsSave?.root ?? null,
    });
  };

  /** An import from the settings screen. The screen goes with the character it was showing. */
  function importedHere(news: ImportNews): void {
    closeSettings();
    settings.applyAll();
    rebuildMenu(news);
  }

  const endSession = (): void => {
    generation += 1;
    stopWatching?.();
    stopWatching = null;
    overlay?.dispose();
    overlay = null;
    session?.stop();
    session = null;
    notices?.dispose();
    notices = null;
  };

  /**
   * The notice stack, mounted once per session rather than once per screen.
   *
   * It is created when a lobby opens and survives the move into the game, because who joined and
   * who dropped is the same feed either side of the Start button, and a stack torn down and rebuilt
   * would swallow the line that arrived during the handover.
   */
  const ensureNotices = (): SessionNotices => {
    notices ??= createSessionNotices(container);
    return notices;
  };

  /**
   * Starts a mode, and watches the session it is being played in.
   *
   * Takes the loaded code rather than the mode, so nothing below this line waits on a network: by
   * the time a game starts, the download that decided whether it could has already been answered.
   *
   * The watch and the notices belong here rather than in the mode: the shell owns the transition a
   * session ending has to produce, and a mode that knew about either would have to be written again
   * in the second mode and the third.
   */
  const play = (code: ModeCode, started: NetSession): void => {
    const showNotices = ensureNotices();
    const stopNotices = started.onNotice((notice) => {
      showNotices.show(notice);
    });
    const stopSeatWatch = watchSeatChange(started, reseat);

    // Armed again after every recovery, because a session can lose its host twice and the watch is
    // deliberately a one-shot: it exists to answer the first of two signals and ignore the second.
    let stopEndWatch: (() => void) | null = null;
    const armEndWatch = (): void => {
      stopEndWatch = watchSessionEnd(started, (reason: ExitReason): void => {
        const lobby = live;
        if (lobby === null) {
          openMenu(reason);
          return;
        }
        void recover(lobby).then((back: boolean) => {
          if (back) armEndWatch();
          else openMenu(reason);
        });
      });
    };
    armEndWatch();

    stopWatching = (): void => {
      stopNotices();
      stopSeatWatch();
      stopEndWatch?.();
    };
    session = code.launch({ container, session: started, onExit: openMenu, profile });
  };

  /**
   * The wait, over the game rather than instead of it.
   *
   * Nothing is torn down while this runs. The mode keeps drawing the last world that arrived, the
   * guest link has already thrown away everything measured against the connection that went, and
   * the lobby knocks on the same code until the seat hold runs out. Resolves true when the game can
   * carry on.
   */
  async function recover(lobby: LobbySession): Promise<boolean> {
    const panel = createReconnectOverlay(container, (): void => {
      lobby.cancelRejoin();
    });
    overlay = panel;
    panel.show(SEAT_HOLD_MS);
    const stopCountdown = lobby.subscribe((state) => {
      if (state.rejoiningMsLeft !== null) panel.show(state.rejoiningMsLeft);
    });

    await lobby.rejoin();

    stopCountdown();
    panel.dispose();
    overlay = null;
    return lobby.transport() !== null;
  }

  /**
   * The one reconnect a running mode cannot absorb: back in the session, as a different player.
   *
   * Rebuilt rather than resumed, on the facts the host just sent, which is what every reconnect did
   * before the swap landed. The lobby session is untouched, so this is a mode restarting rather
   * than a session starting again.
   */
  function reseat(change: SeatChange): void {
    const lobby = live;
    if (lobby === null) return;

    const mode = findMode(change.modeId);
    const restarted = mode === null ? null : sessionFromLobby(lobby, change.facts);
    if (mode === null || restarted === null) {
      openMenu(exitReason('hostLeft'));
      return;
    }

    endSession();
    live = lobby;
    // Resolves in a microtask: a game was running, so this mode's chunk is already in the module
    // cache and nothing is downloaded here. The await is what the contract costs, not a wait.
    const era = generation;
    void mode.load().then((code) => {
      if (era === generation) play(code, restarted);
    }, modeUnavailable);
  }

  /**
   * A mode that could not be fetched, from a screen the player is about to leave.
   *
   * A hashed chunk can be gone after a deploy, so this is a real path and not only an offline one.
   * It is not an `ExitReason`: those carry a code from the failure catalogue and a download is not
   * a session failure, so it goes through the slot that carries a line to the next menu.
   */
  const modeUnavailable = (): void => {
    pendingNotice = MODE_UNAVAILABLE;
    openMenu();
  };

  // The menu and the lobby are both torn down and rebuilt several times a session, so neither can
  // be the thing that remembers who you are. The profile is, and a reroll is written there rather
  // than held in this function: it is the one save point that exists before the hub does.
  const keepName = (name: string): void => {
    profile.rename(name);
  };

  const closeLobby = (): void => {
    lobby?.dispose();
    lobby = null;
  };

  // A line for the next menu, from a screen that will not be there to say it. An import rebuilds
  // the menu around a different character and the receipt for it has to survive the rebuild; a mode
  // that would not download is the same shape, said from a lobby that is being torn down.
  let pendingNotice: MenuNotice | null = null;

  const takePendingNotice = (): MenuNotice | null => {
    const notice = pendingNotice;
    pendingNotice = null;
    return notice;
  };

  // Said once, on the first menu, and not on every trip back to it. A player who cannot save is
  // owed the sentence before they spend an hour, not a banner every time they leave a game.
  let storageWarned = false;

  const storageNotice = (): MenuNotice | null => {
    if (profile.durable() || storageWarned) return null;
    storageWarned = true;
    return SAVE_NO_STORAGE;
  };

  /**
   * Escape: the pause, and the way back out of it.
   *
   * On the document rather than on the mode, because the pointer is locked to the mode's canvas
   * and the browser has already taken the lock away by the time this key arrives. That is also why
   * Escape and not another key: it is the one press a locked pointer is guaranteed to be able to
   * make, and the screen it raises is usable with the mouse it just freed.
   *
   * Only from a running game, and only closed from anywhere. The menu has a button, and Escape on
   * the menu belongs to whatever the player is typing a join code into.
   */
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    if (settingsScreen !== null) {
      closeSettings();
      return;
    }
    if (session !== null) openSettings();
  };

  const openMenu = (reason?: ExitReason): void => {
    closeSettings();
    endSession();
    closeLobby();
    live = null;
    if (menu !== null) return;

    // Built here rather than in the menu because the profile is here, and rebuilt with every menu
    // for the same reason the roller is: neither can be the thing that remembers who you are.
    saveTransfer = createSaveTransfer({ profile, onImported: rebuildMenu });
    // Built with the menu and thrown away with it, like the save panel and the roller, because the
    // character it draws is the shell's and a rebuilt menu must not be drawing a stale one.
    upgrade = createUpgradePanel({ profile });

    menu = createMainMenu(container, {
      modes: MODES,
      onSolo: (mode: GameMode): void => {
        void launch(mode);
      },
      onHost: (mode: GameMode): void => {
        openLobby({ kind: 'host', mode });
      },
      onJoin: (code: string): void => {
        openLobby({ kind: 'join', code });
      },
      playerName: profile.name(),
      onRollName: keepName,
      onSettings: openSettings,
      saveTransfer: saveTransfer.root,
      upgrade: upgrade.root,
      notice: noticeFor(reason) ?? takePendingNotice() ?? storageNotice(),
    });

    // After the menu rather than before it, because the scene is downloaded and the menu is not,
    // so there is no ordering to win. Paint order does not decide it either way: the menu is fixed
    // at `z-index: 10` and draws its own scrim inside that stacking context, so a canvas appended
    // later still lands underneath.
    scenery.show();
  };

  /**
   * An import replaced the character, so the screen showing its name is rebuilt around the new one.
   *
   * Cheaper than it sounds and more honest than a targeted update: the menu is torn down and
   * rebuilt on every transition anyway, and a panel that patched the name row would leave anything
   * else the menu says about a character to be found later, one field at a time.
   */
  function rebuildMenu(news: ImportNews): void {
    if (menu === null) return;
    // The character changed, so the knobs did: settings are stored on the profile, and the one that
    // just landed has its own.
    settings.applyAll();
    pendingNotice = news;
    closeMenu();
    openMenu();
  }

  // The lobby keeps the backdrop: it is a menu screen, not a game, and tearing the canvas down and
  // back up to show eight characters would cost a WebGL context for nothing.
  function openLobby(intent: LobbyIntent, existing: LobbySession | null = null): void {
    closeSettings();
    menu?.dispose();
    menu = null;
    saveTransfer?.dispose();
    saveTransfer = null;
    upgrade?.dispose();
    upgrade = null;
    closeLobby();
    const showNotices = ensureNotices();
    // The host already knows which mode this room is for, so its chunk downloads while people are
    // still joining. Waiting until Start would make the whole room wait on one machine's network.
    if (intent.kind === 'host') void intent.mode.load().catch(() => undefined);
    lobby = createCoopLobby(container, {
      intent,
      session: existing ?? undefined,
      onExit: openMenu,
      playerName: profile.name(),
      onRollName: keepName,
      playerSheet: profileSheet(profile.current()),
      onNotice: (notice): void => {
        showNotices.show(notice);
      },
      onStart: (started: NetSession, modeId: string, from: LobbySession): void => {
        // A mode id this build has never heard of means the host is running something else.
        const mode = findMode(modeId);
        if (mode === null) {
          openMenu(exitReason('versionMismatch'));
          return;
        }
        // Fetched before anything is torn down, so the lobby stays on screen through the wait and
        // a download that fails has a screen to fail from.
        const era = generation;
        void mode.load().then((code) => {
          if (era !== generation) return;
          live = from;
          closeLobby();
          closeMenu();
          play(code, started);
        }, modeUnavailable);
      },
    });
  }

  /**
   * The press on a card: fetch the mode, open the session, then leave the menu.
   *
   * The menu stays up and inert through both, rather than being closed first. A screen torn down
   * before the thing replacing it exists has nowhere to put a failure, and this one now has two
   * ways to fail: a chunk that never arrived and a session that would not open. They are different
   * sentences, which is why the two are settled rather than raced.
   */
  async function launch(mode: GameMode): Promise<void> {
    const open = menu;
    if (open === null) return;

    const era = generation;
    open.setBusy(mode.id);
    const [code, solo] = await Promise.allSettled([
      mode.load(),
      openSoloSession(soloSeed(), undefined, profile.name(), profileSheet(profile.current())),
    ]);

    const started = solo.status === 'fulfilled' ? solo.value : null;
    if (era !== generation || session !== null || menu !== open) {
      abandon(started);
      return;
    }

    if (code.status === 'fulfilled' && started !== null) {
      closeMenu();
      play(code.value, started);
      return;
    }

    // Back to the screen they pressed from, with the half that failed named. A session that would
    // not open has already said why through its own notice, so only the download speaks here.
    abandon(started);
    open.setBusy(null);
    if (code.status === 'rejected') open.notify(MODE_UNAVAILABLE);
  }

  /** A session opened for a game that is not going to happen. Closed rather than left holding. */
  function abandon(started: NetSession | null): void {
    void started?.transport()?.close('the game was never started');
  }

  return {
    start(): () => void {
      document.addEventListener('keydown', onKeyDown);
      openMenu();
      // A link that names a mode presses its card for you. The menu is opened first rather than
      // skipped, because `launch` needs a screen to hold the wait on and to fail back to, and
      // because a link to a mode that would not download has to land somewhere a player can act.
      const asked = modeFromUrl(window.location.search);
      if (asked !== null) void launch(asked);
      return (): void => {
        document.removeEventListener('keydown', onKeyDown);
        closeSettings();
        closeMenu();
        closeLobby();
        endSession();
      };
    },
  };
}

function exitReason(code: NetErrorCode): ExitReason {
  return { code, message: netErrorMessage(code), advice: netErrorAdvice(code) ?? '' };
}

/** A player who chose to leave is told nothing. They already know why they are here. */
function noticeFor(reason: ExitReason | undefined): MenuNotice | null {
  if (reason === undefined) return null;
  return { message: reason.message, advice: reason.advice };
}
